import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { BadgeCheck } from 'lucide-react';

interface VerifiedBadgeProps {
  className?: string;
  /** Show as inline icon only (no tooltip, no label). */
  iconOnly?: boolean;
  /**
   * Render as a filled pill with a "Verified" label instead of a bare icon.
   * Much more noticeable — use in cards and detail headers.
   */
  label?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const iconSizeClasses = {
  sm: 'size-3.5',
  md: 'size-4',
  lg: 'size-5',
};

const pillSizeClasses = {
  sm: 'text-[11px] gap-1 px-1.5 py-0.5',
  md: 'text-xs gap-1 px-2 py-0.5',
  lg: 'text-sm gap-1.5 px-2.5 py-1',
};

const pillIconClasses = {
  sm: 'size-3',
  md: 'size-3.5',
  lg: 'size-4',
};

export function VerifiedBadge({
  className,
  iconOnly = false,
  label = false,
  size = 'md',
}: VerifiedBadgeProps) {
  // Filled, labeled pill — the loud version.
  if (label) {
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full font-semibold shrink-0',
          'bg-bch text-bch-foreground shadow-sm',
          pillSizeClasses[size],
          className,
        )}
        aria-label="Verified BCH merchant"
      >
        <BadgeCheck className={pillIconClasses[size]} strokeWidth={2.5} />
        Verified
      </span>
    );
  }

  const icon = (
    <span
      className={cn(
        'inline-flex items-center justify-center text-bch shrink-0',
        className,
      )}
      aria-label="Verified merchant"
    >
      <BadgeCheck className={iconSizeClasses[size]} strokeWidth={2.5} />
    </span>
  );

  if (iconOnly) return icon;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {icon}
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        <div className="flex items-center gap-1.5">
          <BadgeCheck className="size-3.5 text-bch" />
          <span>Verified BCH Merchant</span>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
